import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Heart, Users, BookOpen, Calendar, HandHeart, 
  ArrowRight, MapPin, Download, MessageSquare, Shield,
  Sparkles, ChevronDown
} from 'lucide-react';

const COMMUNITY_IMG_1 = "https://customer-assets.emergentagent.com/job_0e3427ab-d5b3-4fa8-993a-18bab96ac733/artifacts/64sfcjuv_IMG_6507-compressed.jpeg";
const VOLUNTEERS_IMG = "https://customer-assets.emergentagent.com/job_0e3427ab-d5b3-4fa8-993a-18bab96ac733/artifacts/jkg8o685_IMG_5732-compressed.jpeg";
const COMMUNITY_IMG_2 = "https://customer-assets.emergentagent.com/job_0e3427ab-d5b3-4fa8-993a-18bab96ac733/artifacts/8hosln5z_IMG_6516-compressed.jpeg";

const hubFeatures = [
  {
    icon: BookOpen,
    title: 'Education & Information',
    description: 'Learn about autism from a neurodiversity perspective with guides, articles, and resources.',
    href: '/learn',
    color: 'bg-[hsl(346,85%,55%)]'
  },
  {
    icon: MapPin,
    title: 'Service Directory',
    description: 'Find therapists, specialists, schools, and support services in the Cayman Islands.',
    href: '/directory',
    color: 'bg-[hsl(27,100%,55%)]'
  },
  {
    icon: Download,
    title: 'Tools & Downloads',
    description: 'Visual schedules, social stories, accommodation templates, and more.',
    href: '/resources',
    color: 'bg-[hsl(16,100%,66%)]'
  },
  {
    icon: MessageSquare,
    title: 'Community Forum',
    description: 'Connect with other families, share experiences, and find peer support.',
    href: '/community',
    color: 'bg-[hsl(350,100%,72%)]'
  },
  {
    icon: Calendar,
    title: 'Events & Webinars',
    description: 'Join our monthly webinars with Chair Chelsea and family-friendly events.',
    href: '/events',
    color: 'bg-[hsl(35,100%,58%)]'
  },
  {
    icon: Shield,
    title: 'Training & Advocacy',
    description: 'Autism acceptance training for schools, workplaces, and organisations.',
    href: '/training',
    color: 'bg-[hsl(6,93%,71%)]'
  }
];

// Fundraising data
const FUNDRAISING_GOAL = 60000;
const FUNDRAISING_RAISED = 0;

export default function Dashboard() {
  const { user } = useAuth();
  const progressPercent = (FUNDRAISING_RAISED / FUNDRAISING_GOAL) * 100;

  return (
    <div className="min-h-screen">
      {/* Welcome Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(346,85%,95%)] via-background to-[hsl(27,100%,95%)] section-spacing">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-[hsl(346,85%,90%)] text-[hsl(346,85%,35%)] border-[hsl(346,85%,70%)]">
                <Sparkles className="h-3 w-3 mr-1" />
                Welcome
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground">
                Hello, {user?.first_name}!
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                Welcome to the Autism Acceptance Resources Hub. Explore our resources, connect with the community, 
                and find the support you need.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/directory">
                  <Button className="btn-primary" size="lg" data-testid="explore-directory-btn">
                    Explore Directory
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/community">
                  <Button variant="outline" size="lg" className="btn-secondary" data-testid="join-community-btn">
                    Join the Discussion
                  </Button>
                </Link>
              </div>
              {/* Scroll Down Arrow */}
              <button 
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex flex-col items-center gap-2 text-muted-foreground hover:text-primary transition-colors animate-bounce mt-4"
              >
                <span className="text-sm">Explore features</span>
                <ChevronDown className="h-6 w-6" />
              </button>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src={COMMUNITY_IMG_1} 
                  alt="Autism Acceptance community event" 
                  className="rounded-2xl shadow-lg w-full h-48 object-cover"
                />
                <img 
                  src={COMMUNITY_IMG_2} 
                  alt="Autism Acceptance volunteers" 
                  className="rounded-2xl shadow-lg w-full h-48 object-cover mt-8"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fundraising Banner with Progress Bar */}
      <section className="bg-gradient-to-r from-[hsl(346,85%,55%)] to-[hsl(27,100%,55%)] text-white py-10" data-testid="fundraising-banner">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white border-none">Major Fundraising Goal</Badge>
            <h2 className="text-2xl md:text-3xl font-bold">
              Help Us Build Cayman's First Neurodivergent Community Centre
            </h2>
            <p className="text-white/90">
              We are raising vital funds to establish a safe and welcoming space designed for autistic individuals 
              to learn, connect, and thrive. The centre will include a sensory room, educational workshops, social support resources, 
              and respite opportunities for families.
            </p>
            
            {/* Progress Bar */}
            <div className="max-w-xl mx-auto space-y-3">
              <div className="flex justify-between text-sm">
                <span>Raised: ${FUNDRAISING_RAISED.toLocaleString()}</span>
                <span>Goal: ${FUNDRAISING_GOAL.toLocaleString()}</span>
              </div>
              <div className="h-4 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(progressPercent, 2)}%` }}
                />
              </div>
              <p className="text-white/80 text-sm">
                ${(FUNDRAISING_GOAL - FUNDRAISING_RAISED).toLocaleString()} still needed to secure a lease
              </p>
            </div>

            <div className="flex justify-center gap-4 flex-wrap pt-2">
              <a href="https://donate.autismacceptance.ky/" target="_blank" rel="noopener noreferrer">
                <Button className="bg-white text-[hsl(346,85%,45%)] hover:bg-white/90" size="lg" data-testid="donate-hero-btn">
                  <Heart className="mr-2 h-5 w-5" />
                  Donate Now
                </Button>
              </a>
              <Link to="/about#community-centre">
                <Button variant="secondary" size="lg" className="bg-white/10 text-white hover:bg-white/20 border-white/30">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Access Features */}
      <section id="features" className="section-spacing scroll-mt-20" data-testid="features-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-[hsl(27,100%,90%)] text-[hsl(27,100%,35%)]">Quick Access</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Explore the Hub</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Access all our resources and connect with families across the Cayman Islands.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hubFeatures.map((feature) => (
              <Link key={feature.title} to={feature.href}>
                <Card className="card-base h-full border-t-4 border-t-transparent hover:border-t-primary transition-all hover:shadow-lg cursor-pointer">
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-2`}>
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-spacing bg-muted/30">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={VOLUNTEERS_IMG} 
                alt="Autism Acceptance team and volunteers" 
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
            <div className="space-y-6">
              <Badge className="bg-[hsl(27,100%,90%)] text-[hsl(27,100%,35%)]">Get Involved</Badge>
              <h2 className="text-3xl md:text-4xl font-bold">
                Join Our Volunteer Team
              </h2>
              <p className="text-muted-foreground">
                Our events and initiatives are made possible by dedicated volunteers who share our 
                vision of acceptance and inclusion. From sensory-friendly family events to awareness 
                campaigns, our volunteers are the heart of our community.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/volunteer">
                  <Button className="btn-primary">
                    Become a Volunteer
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="btn-secondary">
                    Learn Our Story
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
